import { memo, useCallback, useMemo } from "react";
import "@visioni/a3s-ui/theme";
import { MultiSelect } from "@visioni/a3s-ui/components";
import type { CameraAccessListCamera } from "../CameraAccessList";

export type CameraMultiSelectProps = {
  cameras: CameraAccessListCamera[];
  selected: Map<string, "manager" | "viewer">;
  onToggle: (id: string) => void;
};

const getOptionId = (camera: CameraAccessListCamera): string => camera.id;
const getOptionLabel = (camera: CameraAccessListCamera): string => camera.name;
const getGroup = (camera: CameraAccessListCamera): string => camera.siteName;

function filterCameras(
  cameras: readonly CameraAccessListCamera[],
  inputValue: string,
): readonly CameraAccessListCamera[] {
  if (!inputValue) return cameras;
  const query = inputValue.toLowerCase();
  return cameras.filter(
    (camera) => camera.name.toLowerCase().includes(query) || camera.id.toLowerCase().includes(query),
  );
}

export const CameraMultiSelect = memo(({ cameras, selected, onToggle }: CameraMultiSelectProps) => {
  // Legacy cameras can't be toggled (see CameraAccessList), so they're not searchable/selectable here either.
  // Sorted by site so MultiSelect's groupBy (which assumes same-group options are contiguous) renders correctly.
  const options = useMemo(
    () =>
      cameras
        .filter((camera) => !camera.legacy)
        .slice()
        .sort((a, b) => a.siteName.localeCompare(b.siteName) || a.name.localeCompare(b.name)),
    [cameras],
  );

  const value = useMemo(() => options.filter((camera) => selected.has(camera.id)), [options, selected]);

  const handleChange = useCallback(
    (nextValue: readonly CameraAccessListCamera[]) => {
      const nextIds = new Set(nextValue.map(getOptionId));
      for (const camera of value) {
        if (!nextIds.has(camera.id)) onToggle(camera.id);
      }
      for (const id of nextIds) {
        if (!selected.has(id)) onToggle(id);
      }
    },
    [value, selected, onToggle],
  );

  return (
    <MultiSelect
      options={options}
      value={value}
      onChange={handleChange}
      getOptionId={getOptionId}
      getOptionLabel={getOptionLabel}
      filterOptions={filterCameras}
      groupBy={getGroup}
      filterSelectedOptions
      placeholder="Search cameras…"
      label="Cameras"
    />
  );
});

CameraMultiSelect.displayName = "CameraMultiSelect";
